import { toast } from 'sonner'
import { Label } from '@teach/ui'
import type { VideoBlock } from '@/types/blocks'
import { VideoUpload } from '@/features/editor/components/shared/VideoUpload'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useDeleteVideoMutation } from '@/features/editor/videosApi'
import { PostersTab } from './poster-chapters/PostersTab'
import { ChaptersTab } from './poster-chapters/ChaptersTab'
import { HintBox } from './design/HintBox'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Постер скрыт по решению заказчика (пока); главы остаются. */
const SHOW_POSTER = false

interface Props {
  // в рантайме приходит полный Block (с id) — id нужен для block_id при удалении видео
  block: VideoBlock & { id: number }
  onChange: (next: VideoBlock) => void
  isCourseAuthor: boolean
  // id роликов, реально сохранённых на сервере. Постер и главы доступны только
  // для привязанного (сохранённого) видео — иначе GET /videos/{id} отдаёт 404.
  savedVideoId: string | null
  savedVideoIdEn: string | null
  /** Загрузка ролика закончилась — экран сохраняется сам. */
  onAutoSave?: () => void
}

/**
 * Панель видео-блока: загрузка, предпросмотр ролика плеером, постер и главы —
 * всё здесь же, без отдельного окна. Слот один, по языку правки (п.8).
 */
export const VideoBlockEditor = ({
  block,
  onChange,
  isCourseAuthor,
  savedVideoId,
  savedVideoIdEn,
  onAutoSave,
}: Props) => {
  const m = useMessages(messages)
  const [deleteVideo] = useDeleteVideoMutation()
  const contentLang = useContentLang()
  const slot: 'videoId' | 'videoIdEn' = contentLang === 'ru' ? 'videoId' : 'videoIdEn'
  const currentVideoId = block[slot]
  const savedForSlot = contentLang === 'ru' ? savedVideoId : savedVideoIdEn
  // Блок сохраняется сам сразу после загрузки — до этого мига постера и глав нет.
  const mediaVideoId = currentVideoId && currentVideoId === savedForSlot ? currentVideoId : null

  // Удаляет ролик в Kinescope (DELETE /videos/{id}?block_id=) и обнуляет слот.
  // На ошибку — toast, слот не трогаем.
  const removeSlot = async () => {
    const id = block[slot]
    if (!id) return
    try {
      await deleteVideo({ id, blockId: block.id }).unwrap()
      onChange({ ...block, [slot]: null })
    } catch {
      toast.error(m.videoDeleteFailed)
    }
  }

  return (
    <div className="space-y-5">
      {/* Телефон/планшет: пустой слот заполняют прямо на экране урока (CanvasBridgeDrop),
          а заменить или удалить уже загруженный ролик можно только здесь — холст с
          роликом показывает плеер, не загрузку. */}
      <section className={currentVideoId ? 'space-y-2' : 'hidden space-y-2 lg:block'}>
        <Label className="font-medium text-foreground">{m.video}</Label>
        <VideoUpload
          videoId={currentVideoId}
          title={m.video}
          slot={slot}
          disabled={!isCourseAuthor}
          onUploaded={(videoId) => {
            onChange({ ...block, [slot]: videoId })
            onAutoSave?.()
          }}
          onRemove={() => void removeSlot()}
        />
        {currentVideoId ? null : <HintBox>{m.videoHint}</HintBox>}
      </section>

      {mediaVideoId && isCourseAuthor ? (
        <>
          {SHOW_POSTER ? (
            <section className="hidden space-y-2 lg:block">
              <Label className="font-medium text-foreground">{m.poster}</Label>
              <PostersTab videoId={mediaVideoId} />
            </section>
          ) : null}
          <section className="space-y-2">
            <Label className="font-medium text-foreground">{m.chapters}</Label>
            <ChaptersTab videoId={mediaVideoId} />
          </section>
        </>
      ) : null}
    </div>
  )
}
